export {}

declare global {
	interface Array<T> {
		toDictionary(key: keyof T): { [key: string]: T };
		toDictionaryMap(keyFunction: (item: T) => string): { [key: string]: T };
		toDictionaryMapValue<TV>(keyFunction: (item: T) => string, valueFunction: (item: T) => TV): { [key: string]: TV };

		remove(removeItem: T): void;
		removeWhere(removeFunction: (item: T) => boolean): void;
		firstOrDefault(where: (item: T) => boolean): T;

		/*
		* Sorts arrray by key (Optionally descending)
		*/
		sortBy(key: keyof T, desc?: boolean): T[];

		/*
		* Sorts arrray by value property on key object (Optionally descending)
		*/
		sortByValue(key: keyof T, desc?: boolean): T[];

		/*
		* Sorts arrray by key (Optionally descending)
		*/
		sortByThen({ key, desc }: { key: keyof T, desc?: boolean },
			{ thenKey, thenDesc }: { thenKey: keyof T, thenDesc?: boolean }): T[];

		/*
		* Creates new array with only distinct members of other array
		*/
		distinct(key?: keyof T | null): T[];

		/**
		 * Find item and pull member
		 * @param predicate Predicate to find item
		 * @param get Function to pull value
		 */
		findAndGet<TV>(predicate: (item: T) => boolean, get: (item: T) => TV, defaultValue: TV): TV;
	}
}

if (!Array.prototype.toDictionary) {
	Array.prototype.toDictionary = function toDictionary<T> (key: keyof T) {
		const dictionary: any = {}
		for (let i = 0; i < this.length; i++) {
			const keyValue = this[i][key]
			dictionary[keyValue] = this[i]
		}
		return dictionary as { [key: string]: T }
	}
}

if (!Array.prototype.toDictionaryMap) {
	Array.prototype.toDictionaryMap = function toDictionaryMap<T> (keyFunction: (item: T) => string): { [key: string]: T } {
		return this.toDictionaryMapValue(keyFunction, v => v)
	}

	Array.prototype.toDictionaryMapValue = function toDictionaryMap<T, TV> (keyFunction: (item: T) => string, valueFunction: (item: T) => TV): { [key: string]: TV } {
		const dictionary: any = {}
		for (let i = 0; i < this.length; i++) {
			const keyValue = keyFunction(this[i])
			dictionary[keyValue] = valueFunction(this[i])
		}
		return dictionary as { [key: string]: TV }
	}
}

if (!Array.prototype.sortBy) {
	Array.prototype.sortBy = function sortBy<T> (this: T[], key: keyof T, desc: boolean = false): T[] {
		return this.sort((a: T, b: T) => {
			const aValue = a[key] as any
			const bValue = b[key] as any

			if (typeof aValue === "string" && typeof bValue === "string") {
				const compare = aValue.localeCompare(bValue)
				return desc ? compare * -1 : compare
			} else if (typeof aValue === "string") {
				return desc ? -1 : 1
			} else if (typeof bValue === "string") {
				return desc ? 1 : -1
			} else {
				if (desc) {
					return bValue - aValue
				} else {
					return aValue - bValue
				}
			}
		})
	}
}

if (!Array.prototype.sortByValue) {
	Array.prototype.sortByValue = function sortBy<T> (this: T[], key: keyof T, desc: boolean = false): T[] {
		return this.sort((a: T, b: T) => {
			let aValue = (a[key] as any).value
			let bValue = (b[key] as any).value

			if (typeof (aValue) === "object" && (a[key] as any).label !== null) {
				aValue = (a[key] as any).label
			}

			if (typeof (bValue) === "object" && (b[key] as any).label !== null) {
				bValue = (b[key] as any).label
			}

			if (typeof aValue === "string" && typeof bValue === "string") {
				const compare = aValue.localeCompare(bValue)
				return desc ? compare * -1 : compare
			} else if (typeof aValue === "string") {
				return desc ? -1 : 1
			} else if (typeof bValue === "string") {
				return desc ? 1 : -1
			} else {
				if (desc) {
					return bValue - aValue
				} else {
					return aValue - bValue
				}
			}
		})
	}
}

if (!Array.prototype.sortByThen) {
	Array.prototype.sortByThen = function sortByThen<T> (this: T[], { key, desc }: { key: keyof T, desc?: boolean },
		{ thenKey, thenDesc }: { thenKey: keyof T, thenDesc?: boolean }): T[] {
		return this.sort((a: T, b: T) => {
			const aValue = a[key] as any
			const bValue = b[key] as any

			if (typeof a[key] === "string") {
				let compare = aValue.localeCompare(bValue)

				if (compare === 0) {
					const aValueThen = a[thenKey] as any
					const bValueThen = b[thenKey] as any

					compare = aValueThen.localeCompare(bValueThen)
				}

				return desc ? compare * -1 : compare
			} else {
				let compare = aValue - bValue

				if (aValue === bValue) {
					const aValueThen = a[thenKey] as any
					const bValueThen = b[thenKey] as any

					compare = aValueThen - bValueThen
				}

				return desc ? compare * -1 : compare
			}
		})
	}
}

if (!Array.prototype.distinct) {
	Array.prototype.distinct = function distinct<T> (this: T[], key: keyof T | null = null): T[] {
		const newArray = []
		const addedValues = new Set()

		var i = 0
		if (key) {
			while (i < this.length) {
				if (!addedValues.has(this[i][key])) {
					addedValues.add(this[i][key])
					newArray.push(this[i])
				}
				i++
			}
		} else {
			while (i < this.length) {
				if (!addedValues.has(this[i])) {
					addedValues.add(this[i])
					newArray.push(this[i])
				}
				i++
			}
		}

		return newArray
	}
}

if (!Array.prototype.remove) {
	Array.prototype.remove = function remove<T> (this: T[], removeItem: T): void {
		const removeIndex = this.indexOf(removeItem)
		if (removeIndex > -1) {
			this.splice(removeIndex, 1)
		}
	}
}

if (!Array.prototype.removeWhere) {
	Array.prototype.removeWhere = function removeWhere<T> (this: T[], removeFunction: (item: T) => boolean): void {
		var i = 0
		while (i < this.length) {
			if (removeFunction(this[i])) {
				this.splice(i, 1)
			} else {
				++i
			}
		}
	}
}

if (!Array.prototype.firstOrDefault) {
	Array.prototype.firstOrDefault = function T<T> (this: T[], where: (item: T) => boolean): T | null {
		if (where) {
			var i = 0
			while (i < this.length) {
				if (where(this[i])) {
					return this[i]
				}
				i++
			}
			return null
		} else {
			return this.length > 0 ? this[0] : null
		}
	}
}

if (!Array.prototype.findAndGet) {
	Array.prototype.findAndGet = function <T, TV> (this: T[], predicate: (item: T) => boolean, get: (item: T) => TV, defaultValue: TV): TV {
		const item = this.find(predicate)
		return item ? get(item) : defaultValue
	}
}
